import React, { useState, useEffect } from 'react';
import { User, Pill, Microscope, AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';

interface PatientInfo {
  id: string;
  name: string;
  mrn: string;
  age: number;
  conditions: string[];
  medications: string[];
  recent_labs: Array<{
    name: string;
    value: number;
    unit: string;
    reference_range: string;
    trend: 'up' | 'down' | 'stable';
    baseline: number;
    date: string;
  }>;
}

interface PatientContextProps {
  patientId: string;
  onClose: () => void;
}

const PatientContext: React.FC<PatientContextProps> = ({ patientId, onClose }) => {
  const [patient, setPatient] = useState<PatientInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPatientData = async () => {
      try {
        const response = await fetch(`/api/patients/${patientId}`);
        const data = await response.json();
        if (data.success && data.patient) {
          setPatient(data.patient);
        }
      } catch (error) {
        console.error('Error loading patient data:', error);
      }
      setLoading(false);
    };

    loadPatientData();
  }, [patientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">Loading patient information...</div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="p-8 bg-red-50 rounded-lg border border-red-200">
        <p className="text-red-700">Patient information not available</p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold mb-2">{patient.name}</h2>
            <p className="text-blue-100">MRN: {patient.mrn} | Age: {patient.age} years</p>
          </div>
          <button
            onClick={onClose}
            className="text-2xl font-bold hover:bg-blue-500 rounded-full w-10 h-10 flex items-center justify-center"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Active Conditions */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <h3 className="text-lg font-semibold text-gray-900">Active Conditions</h3>
            </div>
            {patient.conditions && patient.conditions.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {patient.conditions.map((condition, idx) => (
                  <div
                    key={idx}
                    className="bg-red-50 border-l-4 border-red-500 p-3 rounded"
                  >
                    <p className="text-sm font-medium text-red-900">{condition}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No active conditions recorded</p>
            )}
          </section>

          {/* Current Medications */}
          <section>
            <div className="flex items-center gap-2 mb-4">
              <Pill className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Current Medications</h3>
            </div>
            {patient.medications && patient.medications.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {patient.medications.map((med, idx) => (
                  <div
                    key={idx}
                    className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded"
                  >
                    <p className="text-sm font-medium text-blue-900">{med}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No medications recorded</p>
            )}
          </section>

          {/* Recent Labs */}
          {patient.recent_labs && patient.recent_labs.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-4">
                <Microscope className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Recent Lab Results</h3>
              </div>
              <div className="space-y-3">
                {patient.recent_labs.map((lab, idx) => (
                  <div key={idx} className="bg-gray-50 p-4 rounded border border-gray-200">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900">{lab.name}</p>
                        <p className="text-sm text-gray-600">
                          Baseline: {lab.baseline} {lab.unit} | Normal: {lab.reference_range}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">{lab.value}</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          {lab.trend === 'up' && (
                            <><TrendingUp className="w-4 h-4 text-red-500" /><span className="text-xs text-red-600">Increasing</span></>
                          )}
                          {lab.trend === 'down' && (
                            <><TrendingDown className="w-4 h-4 text-green-500" /><span className="text-xs text-green-600">Decreasing</span></>
                          )}
                          {lab.trend === 'stable' && (
                            <span className="text-xs text-gray-600">Stable</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {lab.value > parseFloat(lab.reference_range.split('-')[1]) ||
                      lab.value < parseFloat(lab.reference_range.split('-')[0]) && (
                      <div className="mt-2 p-2 bg-yellow-50 border-l-2 border-yellow-500 rounded text-xs text-yellow-800">
                        ⚠ Outside normal range
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Footer */}
          <div className="border-t pt-4">
            <button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientContext;
